/**
 * Index.ts
 * This Component is used to write common function to used them throughout the project..
 */

import { DEFAULT_THEME_VARIABLES } from "./constants";
import { TCheckPrevilege, TPrivilege } from "../types/config";
import { TFilterModel } from "../types/config";
import html2pdf from "html2pdf.js";
import dayjs from "dayjs";

declare const document: any;
declare const Element: any;
export default class Utils {
  // function to reterive the param value from url and return the updated object
  static updateParamsFromUrl(
    url: string,
    defaultParams: TFilterModel,
  ): TFilterModel {
    const newUrl = new URL(url);
    const queryParams = newUrl.searchParams;

    const updatedParams = { ...defaultParams };

    queryParams.forEach((value, key) => {
      if (key in updatedParams) {
        (updatedParams as any)[key] = value;
      }
    });

    return updatedParams;
  }

  static generatePDF = (elementId: string, filename: string) => {
    const element = document.getElementById(elementId);
    const opt = {
      margin: 1,
      filename: `${filename}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf().from(element).set(opt).save();
  };

  static onResetFilter(url: string): boolean {
    let isReset = false;
    if (url.includes("?")) {
      const newUrl = url.split("?")[0];
      window.history.pushState({}, "", newUrl.toString());
      isReset = true;
    }
    return isReset;
  }

  static flattenTagMasterList = (inputArray: any[]) => {
    let resultArray: any[] = [];
    let tagLabelNameSet = new Set();

    const flatten = (tag: any, parentGroupName: string | null = null) => {
      // Determine the effective groupName for the tag, using parentGroupName if present
      const effectiveGroupName = tag.groupName || parentGroupName;

      // Check if isTag is true or false
      if (tag?.isTag || !tag?.isTag) {
        // If isTag is true, check for combination of tagLabel and labelName duplicacy
        if (
          tag?.isTag &&
          !tagLabelNameSet.has(
            `${effectiveGroupName}-${tag?.tagLabel}-${tag?.labelName}-${tag?.tagFooter}`,
          )
        ) {
          // Add the combination of tagLabel and labelName to the set
          tagLabelNameSet.add(
            `${effectiveGroupName}-${tag.tagLabel}-${tag.labelName}-${tag?.tagFooter}`,
          );

          // Push the current tag to the result array
          resultArray.push({ ...tag, groupName: effectiveGroupName });
        } else if (!tag.isTag) {
          // If isTag is false, push the tag directly
          resultArray.push({ ...tag, groupName: effectiveGroupName });
        }

        // If the tag has a tagMasterList, recursively flatten it
        if (tag.tagMasterList && Array.isArray(tag.tagMasterList)) {
          tag.tagMasterList.forEach((nestedTag: any) => {
            flatten(
              { ...nestedTag, isLabel: true, labelName: tag.tagLabel },
              effectiveGroupName,
            );
          });
        }
      }
    };

    // Iterate over each tag in the input array and flatten it
    inputArray.forEach(flatten as any);

    return resultArray;
  };

  /**
   * Converts an enum to an array of options with label and value.
   * @param enumObj The enum object to convert.
   * @returns An array of options with label and value.
   */
  static getEnumOptions(enumObj: any): { label: string; value: any }[] {
    return Object.keys(enumObj)
      .filter((key) => !isNaN(Number(enumObj[key as keyof typeof enumObj]))) // Filter numeric keys
      .map((key) => ({
        label: key
          .toLowerCase()
          .replace(/_/g, " ") // Replace underscores with spaces
          .replace(/\b\w/g, (char) => char.toUpperCase()), // Capitalize each word
        value: enumObj[key as keyof typeof enumObj], // Get the value
      }));
  }

  //   // fullscreen mode
  static upFullScreen() {
    // ## The below if statement seems to work better ## if ((document.fullScreenElement && document.fullScreenElement !== null) || (document.msfullscreenElement && document.msfullscreenElement !== null) || (!document.mozFullScreen && !document.webkitIsFullScreen)) {
    if (
      (document.fullScreenElement !== undefined &&
        document.fullScreenElement === null) ||
      (document.msFullscreenElement !== undefined &&
        document.msFullscreenElement === null) ||
      (document.mozFullScreen !== undefined && !document.mozFullScreen) ||
      (document.webkitIsFullScreen !== undefined &&
        !document.webkitIsFullScreen)
    ) {
      if (document.documentElement.requestFullScreen) {
        document.documentElement.requestFullScreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullScreen) {
        document.documentElement.webkitRequestFullScreen(
          Element.ALLOW_KEYBOARD_INPUT,
        );
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen();
      }
    }
  }

  //   // close fullscreen mode
  static downFullScreen() {
    if (document.cancelFullScreen) {
      document.cancelFullScreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }

  /**
   * is null or undefined
   * @param value
   * @returns
   */
  static isNullOrUndefined = (value: any) => [null, undefined].includes(value);

  static deepClone = (value: any = {}) => JSON.parse(JSON.stringify(value));
  static applyThemeColor(theme: any, vars = DEFAULT_THEME_VARIABLES) {
    if (theme) {
      for (let prop of vars) {
        document.documentElement.style.setProperty(`--${prop}`, theme[prop]);
      }
    }
  }

  static getBase64FromByteString = (bytesInString: string = "") => {
    return `data:image/png;base64,${bytesInString}`;
  };

  static getFloatValue = (value: any) =>
    typeof value == "number"
      ? value
      : isNaN(parseFloat(value))
        ? 0
        : parseFloat(value);

  static toUpper = (value: any) =>
    value && typeof value == "string" ? value.toUpperCase() : "";

  static scrollToTop = (top: number = document.documentElement.scrollHeight) =>
    window.scroll({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });

  // static toggleScreen = () => {
  //   ScreenUtil.isFullScreen() ? Utils.downFullScreen() : Utils.upFullScreen();
  // };

  // static async getScreenShot(
  //   elementId: string,
  //   scale: number = 0.5,
  //   quality: number = 0.5
  // ) {
  //   try {
  //     if (elementId) {
  //       var shareContent = document.querySelector(elementId) as HTMLElement; // need screenshots of the package （ the native ）DOM  object
  //       // var shareContent = document.getElementsByClassName('page-1')[0];
  //       var width = shareContent.offsetWidth * scale; // get the dom width
  //       var height = shareContent.offsetHeight * scale; // get the dom height

  //       var canvas = document.createElement("canvas"); // create a canvas node

  //       canvas.width = width * scale; // define canvas width  *  the zoom
  //       canvas.height = height * scale; // define canvas height  * the zoom
  //       canvas.getContext("2d").scale(scale, scale); // get the context, set scale

  //       var opts: any = {
  //         scale, //  the added scale parameter
  //         canvas, // the custom canvas
  //         logging: true, // the log switch
  //         width: width * 2, //dom  the original width
  //         height: height * 2, //dom  original height
  //         allowTaint: true,
  //         backgroundColor: "#f3f3f3",
  //         html2canvas: {},
  //         useCORS: true,
  //       };
  //       const canvasImage = await html2canvas(shareContent, opts);
  //       return canvasImage.toDataURL("image/jpeg", quality);
  //     } else {
  //       return {
  //         error: "Please provide element to capture",
  //       };
  //     }
  //   } catch (error) {
  //     console.error(`Error ! while capturing screen shoot error ${error}`);
  //     return {
  //       error: "Error ! while capturing screen shoot",
  //     };
  //   }
  // }

  static splittedBase64String = (value: string) => {
    if (!value) {
      return {
        error: "Please provide image base 64 string",
      };
    }
    return /,(.+)/.exec(value)?.[1];
  };

  static preventDefault = (event: any = {}) => {
    event?.preventDefault();
    event?.stopPropagation();
    return false;
  };

  static redirectUrl = (path: string) => {
    window.location.href = path;
  };

  static isFalsy = (value: any) =>
    [undefined, null, "", 0, false].includes(value);

  static sleep = (milliseconds: number) => {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  };

  static generateIntRandomBetweenMinMax = (
    min: number = 0,
    max: number = 100,
  ) => Math.floor(Math.random() * (max - min + 1) + min);

  /**
   * Query string
   * @param q params
   * @returns
   */
  static getQueryString = (q: any = {}) => {
    const qParams = Object.keys(q).filter(
      (param) => ![null, undefined].includes(q[param]),
    );
    return qParams.length
      ? "?" +
          qParams
            .map(
              (param) =>
                `${encodeURIComponent(param)}=${encodeURIComponent(q[param])}`,
            )
            .join("&")
      : "";
  };
  static SUCCESS: any;

  static prepareTree(
    data: Array<any> = [],
    parentId = "parentId",
    childrenId = "children",
  ): Array<any> | Object {
    let root: any;
    const idMapping = data.reduce((acc, el, i) => {
      acc[el.id] = i;
      return acc;
    }, {});

    // Deep copy the input data to avoid modifying the original data
    const dataCopy = JSON.parse(JSON.stringify(data));

    dataCopy.forEach((el: any) => {
      if (!el[parentId]) {
        root = el;
        return;
      }
      // Use our mapping to locate the parent element in our copied data array
      const parentEl = dataCopy[idMapping[el[parentId]]];
      // Add our current el to its parent's `children` array
      parentEl[childrenId] = [...(parentEl[childrenId] || []), el];
    });
    return root;
  }

  static mapAdditionalOption(
    options: { value: number; label: string }[],
    selectedOption?: { value: number; label: string },
  ) {
    if (selectedOption) {
      const foundOption = options.find(
        (option) => option?.value === selectedOption?.value,
      );
      return foundOption ? options : options.unshift(selectedOption);
    }
  }

  static isUserHasAccess(
    privilegeList: TPrivilege[],
    componentUniuqId: string,
  ): boolean {
    const userPrivilegeId = privilegeList?.find(
      (items: TPrivilege) => items.privilegeUniqueId === componentUniuqId,
    );

    if (userPrivilegeId) {
      return true;
    }
    return false;
  }

  static mapDropdownOptions(
    inputData: any,
    valueParam = "id",
    labelParam = "name",
  ) {
    return inputData.map((item: { [x: string]: any }) => ({
      value: item[valueParam], // Mapping to the value parameter (default: id)
      label: item[labelParam], // Mapping to the label parameter (default: name)
      data: item, // Original item data
    }));
  }

  static hasDifference(param1: TFilterModel, param2: TFilterModel): boolean {
    return (
      param1.currentPage !== param2.currentPage ||
      param1.pageSize !== param2.pageSize ||
      param1.searchText !== param2.searchText ||
      param1.fromDate !== param2.fromDate ||
      param1.toDate !== param2.toDate
    );
  }

  static debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  static generatePassword(): string {
    const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const specialChars = "!@#$%^&*()_+[]{}|;:,.<>?";

    // Ensure at least one character from each category
    const getRandomChar = (chars: string): string =>
      chars.charAt(Math.floor(Math.random() * chars.length));

    const randomUppercase = getRandomChar(uppercaseChars);
    const randomLowercase = getRandomChar(lowercaseChars);
    const randomNumber = getRandomChar(numbers);
    const randomSpecial = getRandomChar(specialChars);

    // Combine the required characters
    let password =
      randomUppercase + randomLowercase + randomNumber + randomSpecial;

    // Fill the rest of the password with random characters from all categories
    const allChars = uppercaseChars + lowercaseChars + numbers + specialChars;
    while (password.length < 8) {
      password += getRandomChar(allChars);
    }

    // Shuffle the password to ensure randomness
    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    return password;
  }

  static getUniqueFilters(data: any[], key: string) {
    const uniqueValues = Array.from(new Set(data?.map((item) => item?.[key])));
    return uniqueValues.map((value) => ({ text: value, value }));
  }

  /**
   * Robust parser for various date/year formats into dayjs objects
   * @param val The value to parse
   * @returns dayjs object if valid, otherwise the original value or null
   */
  static parseToDayjs(val: any) {
    if (!val) return null;

    // If it's already a dayjs object, return it
    if (dayjs.isDayjs(val)) return val;

    const valStr = val.toString().trim();

    // Try YYYY
    const dYear = dayjs(valStr, "YYYY", true);
    if (dYear.isValid()) return dYear;

    // Try MM/YYYY or MM-YYYY
    const dMonthYear = dayjs(valStr, ["MM/YYYY", "MM-YYYY"], true);
    if (dMonthYear.isValid()) return dMonthYear;

    // Try YYYY-MM-DD (standard)
    const dFull = dayjs(valStr, "YYYY-MM-DD", true);
    if (dFull.isValid()) return dFull;

    // Fallback for numeric timestamps
    const isNumeric = !isNaN(Number(valStr)) && !isNaN(parseFloat(valStr));
    if (isNumeric) {
      const dNum = dayjs(Number(valStr));
      if (dNum.isValid()) return dNum;
    }

    // Last resort generic dayjs
    const dGeneric = dayjs(valStr);
    return dGeneric.isValid() ? dGeneric : null;
  }
}
